import { useCallback, useEffect, useRef, useState } from "react";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Card from "@mui/material/Card";
import Chip from "@mui/material/Chip";
import FutureBoardProgress from "./FutureBoardProgress";
import Icon from "@mui/material/Icon";
import IconButton from "@mui/material/IconButton";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import {
  altTextFor, focalPosition, getFutureAssistQuota, getFutureCard, parseFutureBoardError, talkAvailability,
  type AssistQuota, type FeelingSupport, type FutureBoardFailure, type FutureCard,
} from "@chronospark/shared";
import FailureAlert from "./FailureAlert";
import FeelingDialog from "./FeelingDialog";
import FutureTalkDialog from "./FutureTalkDialog";

type Translate = (key: string, options?: object) => string;

interface Props {
  cardId: number;
  accessToken?: string;
  language: string;
  t: Translate;
  onBack: () => void;
  /** 「描き直す」：編集画面へ */
  onEdit: () => void;
  onFailure: (failure: FutureBoardFailure) => void;
  onNotice: (message: string) => void;
  onSupport?: (support: FeelingSupport) => void;
  /** 「今日の行動を考える」：AI コーチの画面へ（このカードの ID を渡す） */
  onPlanToday?: () => void;
}

const TALK_ONLY: ReadonlySet<FeelingSupport> = new Set(["talk_ai"]);
const NOTHING_HIDDEN: ReadonlySet<FeelingSupport> = new Set();

// 署名付き URL の失効を直すための再取得は、続けて何度もしない。
const IMAGE_REFRESH_INTERVAL_MS = 60_000;

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <Box sx={{ mb: 2.5 }}>
      <Typography variant="subtitle2" color="text.secondary" component="h2" sx={{ mb: 0.5 }}>
        {title}
      </Typography>
      {children}
    </Box>
  );
}

// ワンシーン：1枚のカードを、落ち着いて眺める。カウントダウン・自動再生・強制のアニメーションはない。
function OneScene({ cardId, accessToken, language, t, onBack, onEdit, onFailure, onNotice, onSupport, onPlanToday }: Props) {
  const [card, setCard] = useState<FutureCard | null>(null);
  const [failure, setFailure] = useState<FutureBoardFailure | null>(null);
  const [index, setIndex] = useState(0);
  const [feelingOpen, setFeelingOpen] = useState(false);
  const [talkOpen, setTalkOpen] = useState(false);
  const [quota, setQuota] = useState<AssistQuota | null>(null);
  const lastRefresh = useRef(0);

  // AI（話す・今日の行動を考える）が使えるか。使えない・分からないときは、入口を出さない（手入力は、そのまま使える）。
  const loadQuota = useCallback(async () => {
    if (!accessToken) return;
    try {
      setQuota(await getFutureAssistQuota(accessToken));
    } catch {
      setQuota(null);
    }
  }, [accessToken]);
  useEffect(() => {
    void loadQuota();
  }, [loadQuota]);
  const markDisabled = useCallback(() => setQuota({ enabled: false }), []);
  const aiAvailable = talkAvailability(quota) === "ready" || talkAvailability(quota) === "exhausted";

  const load = useCallback(async (quiet = false): Promise<FutureCard | null> => {
    if (!accessToken) return null;
    try {
      const result = await getFutureCard(accessToken, cardId, { hideGlobalLoader: quiet });
      setCard(result.card);
      setFailure(null);
      return result.card;
    } catch (error) {
      const parsed = parseFutureBoardError(error);
      if (quiet) return null; // 画像の再取得に失敗しても、いまの文章はそのまま表示する
      setFailure(parsed);
      if (parsed.kind === "disabled" || parsed.kind === "unauthorized") onFailure(parsed);
      return null;
    }
  }, [accessToken, cardId, onFailure]);

  useEffect(() => {
    setCard(null);
    setIndex(0);
    void load();
  }, [load]);

  // 画像の署名付き URL が期限切れなどで読み込めないときは、文章はそのままで、画像だけ取得し直す。
  const refreshImages = () => {
    const now = Date.now();
    if (now - lastRefresh.current < IMAGE_REFRESH_INTERVAL_MS) return;
    lastRefresh.current = now;
    const before = (card?.images ?? []).map(image => image.url).join("|");
    void load(true).then(fresh => {
      // 新しい URL が取れたときだけ知らせる（取れなかったのに「読み込み直しました」とは言わない）
      if (fresh && (fresh.images ?? []).map(image => image.url).join("|") !== before) onNotice(t("futureBoard.scene.imageReloaded"));
    });
  };

  if (failure && !card) {
    return (
      <Box>
        <FailureAlert failure={failure} t={t} onReload={() => void load()} />
        <Button onClick={onBack} startIcon={<Icon>arrow_back</Icon>}>{t("futureBoard.scene.backToBoard")}</Button>
      </Box>
    );
  }
  if (!card) {
    return <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}><FutureBoardProgress aria-label={t("Common.loading")} /></Box>;
  }

  const images = [...(card.images ?? [])].sort((a, b) => (a.position ?? 0) - (b.position ?? 0));
  const current = images[Math.min(index, Math.max(images.length - 1, 0))];
  const notWritten = (
    <Typography variant="body2" color="text.disabled">{t("futureBoard.scene.notWritten")}</Typography>
  );

  return (
    <Box>
      <Button onClick={onBack} startIcon={<Icon>arrow_back</Icon>} sx={{ mb: 1 }}>
        {t("futureBoard.scene.backToBoard")}
      </Button>
      {failure && <FailureAlert failure={failure} t={t} onReload={() => void load()} onClose={() => setFailure(null)} />}

      <Card sx={{ overflow: "hidden", borderRadius: 4 }}>
        <Box sx={{ display: "grid", gridTemplateColumns: { xs: "minmax(0, 1fr)", md: current ? "minmax(0, 1.15fr) minmax(0, 1fr)" : "minmax(0, 1fr)" }, alignItems: "start" }}>
          {current ? (
            <Box sx={{ position: "relative", minWidth: 0 }}>
              {current.url ? (
                <Box
                  component="img"
                  src={current.url}
                  alt={altTextFor(current, language, t("futureBoard.image.defaultAlt"), current.alt_text_en)}
                  onError={refreshImages}
                  // 自然な縦横比で列幅に合わせ、画像左右の帯を作らない。縦に長すぎるときだけ、注目位置を中心に切り取る。
                  sx={{ display: "block", width: "100%", height: "auto", maxHeight: { xs: 420, md: 560 }, objectFit: "cover", objectPosition: focalPosition(current) }}
                />
              ) : (
                <Box sx={{ height: 200, display: "flex", alignItems: "center", justifyContent: "center" }}>
                  <Button onClick={refreshImages}>{t("futureBoard.actions.reload")}</Button>
                </Box>
              )}
              {images.length > 1 && (
                <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ px: 1, py: 0.5, bgcolor: "background.paper" }}>
                  <IconButton onClick={() => setIndex(index - 1)} disabled={index <= 0} aria-label={t("futureBoard.scene.prevImage")}>
                    <Icon>chevron_left</Icon>
                  </IconButton>
                  <Typography variant="caption" aria-live="polite">
                    {t("futureBoard.scene.imageCounter", { current: index + 1, total: images.length })}
                  </Typography>
                  <IconButton onClick={() => setIndex(index + 1)} disabled={index >= images.length - 1} aria-label={t("futureBoard.scene.nextImage")}>
                    <Icon>chevron_right</Icon>
                  </IconButton>
                </Stack>
              )}
            </Box>
          ) : null}

          <Box sx={{ p: { xs: 2, sm: 3 } }}>
            <Typography variant="h4" component="h1" sx={{ mb: 1, overflowWrap: "anywhere" }}>
              {card.title}
            </Typography>
            {card.domains.length > 0 && (
              <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap sx={{ mb: 2 }}>
                {card.domains.map(domain => <Chip key={domain} size="small" variant="outlined" label={domain} />)}
              </Stack>
            )}

            <Section title={t("futureBoard.scene.whoIAm")}>
              {card.self_image ? <Typography sx={{ whiteSpace: "pre-wrap", overflowWrap: "anywhere" }}>{card.self_image}</Typography> : notWritten}
            </Section>
            <Section title={t("futureBoard.scene.sceneAndFeeling")}>
              {card.scene ? <Typography sx={{ whiteSpace: "pre-wrap", overflowWrap: "anywhere" }}>{card.scene}</Typography> : notWritten}
              {card.sensations.length > 0 && (
                <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap sx={{ mt: 1 }}>
                  {card.sensations.map(sensation => <Chip key={sensation} size="small" label={sensation} />)}
                </Stack>
              )}
            </Section>
            <Section title={t("futureBoard.scene.meaning")}>
              {card.meaning ? <Typography sx={{ whiteSpace: "pre-wrap", overflowWrap: "anywhere" }}>{card.meaning}</Typography> : notWritten}
            </Section>

          </Box>
        </Box>
        <Stack direction={{ xs: "column", sm: "row" }} spacing={1} flexWrap="wrap" useFlexGap sx={{ p: { xs: 2, sm: 3 }, borderTop: "1px solid", borderColor: "divider" }}>
          <Button variant="outlined" onClick={onBack}>{t("futureBoard.scene.finish")}</Button>
          <Button variant="contained" onClick={() => setFeelingOpen(true)}>
            {t("futureBoard.scene.checkFeeling")}
          </Button>
          <Button variant="outlined" onClick={onEdit}>
            {t("futureBoard.scene.redraw")}
          </Button>
          {aiAvailable && (
            <Button variant="outlined" onClick={() => setTalkOpen(true)}>
              {t("futureBoard.scene.talkAi")}
            </Button>
          )}
          {aiAvailable && onPlanToday && (
            <Button variant="outlined" onClick={onPlanToday}>
              {t("futureBoard.scene.planToday")}
            </Button>
          )}
        </Stack>
      </Card>

      <FeelingDialog
        open={feelingOpen} cardId={card.id} accessToken={accessToken} language={language} t={t} onClose={() => setFeelingOpen(false)}
        hiddenSupports={aiAvailable ? NOTHING_HIDDEN : TALK_ONLY}
        onSupport={support => (support === "talk_ai" ? setTalkOpen(true) : onSupport?.(support))}
      />
      <FutureTalkDialog
        open={talkOpen} cardId={card.id} accessToken={accessToken} language={language} t={t} quota={quota}
        onUsed={loadQuota} onDisabled={markDisabled} onClose={() => setTalkOpen(false)}
      />
    </Box>
  );
}

export default OneScene;
